export type OddsFormat = "decimal" | "american";

// Decimal odds for an American price: −110 → 1.909, +150 → 2.5. Prices
// strictly between −100 and +100 are not valid American odds.
export function americanToDecimal(american: number): number | null {
  if (!Number.isFinite(american)) return null;
  if (american >= 100) return 1 + american / 100;
  if (american <= -100) return 1 + 100 / Math.abs(american);
  return null;
}

// American price for decimal odds: 2.0 → +100, 1.5 → −200.
export function decimalToAmerican(decimal: number): number {
  return decimal >= 2 ? (decimal - 1) * 100 : -100 / (decimal - 1);
}

export function formatAmericanOdds(decimal: number): string {
  const american = Math.round(decimalToAmerican(decimal));
  return american > 0 ? `+${american}` : `−${Math.abs(american)}`;
}

export function toDecimalOdds(
  value: number,
  format: OddsFormat,
): number | null {
  if (format === "american") return americanToDecimal(value);
  return Number.isFinite(value) && value > 1 ? value : null;
}

export type VigResult = {
  vig: number;
  overround: number;
  probabilities: number[];
  noVigOdds: number[];
};

// Removes the margin additively: every outcome gives up an equal share of the
// overround. Returns null when an outcome's implied probability is smaller
// than its share, which happens only with extreme long shots.
export function calculateVig(decimalOdds: readonly number[]): VigResult | null {
  const implied = decimalOdds.map((odds) => 1 / odds);
  const total = implied.reduce((sum, probability) => sum + probability, 0);
  const share = (total - 1) / decimalOdds.length;
  const probabilities = implied.map((probability) => probability - share);
  if (probabilities.some((probability) => probability <= 0)) return null;
  return {
    vig: ((total - 1) / total) * 100,
    overround: (total - 1) * 100,
    probabilities,
    noVigOdds: probabilities.map((probability) => 1 / probability),
  };
}
